import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { Loader2, X, Check, Info } from "lucide-react";
import modalStyles from "../styles/modals.module.css";
import { FormInput } from "@/components/ui/FormField";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { RELATIONSHIP_TYPE_LABELS } from "../types/invitee";
import type { CoApplicant, Invitee } from "../types/invitee";
import { updateInvitation } from "../services/inviteeService";
import {
  editInviteeSchema,
  editCoApplicantSchema,
  validateMobile,
  validateEmailDiff,
  validateMobileDiff,
  getInviteEmailFieldError,
  getInviteFirstNameFieldError,
  getInviteLastNameFieldError,
  CO_APPLICANT_EMAIL_UNIQUE_MESSAGE,
  CO_APPLICANT_PHONE_UNIQUE_MESSAGE,
  MOBILE_PHONE_PATTERN_MESSAGE,
  getIncomeDigitLimitValidationMessage,
  INCOME_MAX_DIGITS_BEFORE_DECIMAL,
} from "../utils/inviteeValidators";
import { formatPhoneNumber } from "../utils/inviteeFormatters";
import { z } from "zod";

interface EditInviteeDialogProps {
  isOpen: boolean;
  invite: Invitee | any | null;
  parentInvite?: Invitee | null;
  type: "edit" | "add";
  onClose: () => void;
  onSuccess: () => void;
}

interface FieldErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  mobile_no?: string;
  relationship_type?: string;
  living_unit?: string;
  is_approved_require?: string;
  income_verified?: string;
  income?: string;
}

/**
 * Title case transformation (matches Angular's titleName pipe)
 * Capitalizes first letter of each word
 */
function toTitleCase(input: string): string {
  if (!input) return "";
  return input.replace(/\w\S*/g, (txt) => txt[0].toUpperCase() + txt.substr(1));
}

export function EditInviteeDialog({
  isOpen,
  invite,
  parentInvite,
  type,
  onClose,
  onSuccess,
}: EditInviteeDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const showCoApplicantFields = type === "add" || !!parentInvite;

  useEffect(() => {
    if (!isOpen) return;

    if (type === "edit" && invite) {
      setValues({
        first_name: invite.first_name || "",
        last_name: invite.last_name || "",
        email: invite.email || "",
        mobile_no: invite.mobile_no || "",
        relationship_type: invite.relationship_type ?? "",
        living_unit: invite.living_unit ?? "",
        is_approved_require: invite.is_approved_require ?? "",
        income_verified: invite.income ? "1" : invite.income_verified || "0",
        income: invite.income || "",
      });
    } else if (type === "add") {
      setValues({
        first_name: "",
        last_name: "",
        email: "",
        mobile_no: "",
        relationship_type: "",
        living_unit: "",
        is_approved_require: "",
        income_verified: "",
        income: "",
      });
    }

    setError(null);
    setFieldErrors({});
    setTouched({});
  }, [isOpen, invite, type]);

  useBodyScrollLock(isOpen);

  // Validate a single field
  const validateField = (
    name: string,
    value: any,
    currentValues?: Record<string, any>,
  ): string | undefined => {
    const vals = currentValues || values;
    switch (name) {
      case "first_name": {
        const firstNameError = getInviteFirstNameFieldError(value);
        if (firstNameError) return firstNameError;
        return undefined;
      }

      case "last_name": {
        const lastNameError = getInviteLastNameFieldError(value);
        if (lastNameError) return lastNameError;
        return undefined;
      }

      case "email": {
        const emailFormatError = getInviteEmailFieldError(value);
        if (emailFormatError) return emailFormatError;
        const primaryEmailForCo =
          parentInvite?.email ?? (type === "add" ? invite?.email : undefined);
        if (showCoApplicantFields && primaryEmailForCo && value) {
          if (!validateEmailDiff(value, primaryEmailForCo)) {
            return CO_APPLICANT_EMAIL_UNIQUE_MESSAGE;
          }
        }
        if (showCoApplicantFields && value && invite) {
          const siblingEmails: string[] =
            type === "add" && invite.co_applicant?.length
              ? invite.co_applicant.map((c: CoApplicant) => c.email).filter(Boolean) as string[]
              : parentInvite?.co_applicant?.length && invite.invite_code
                ? (parentInvite.co_applicant as { invite_code?: string; email: string }[])
                    .filter((c) => c.invite_code !== invite.invite_code)
                    .map((c) => c.email)
                    .filter(Boolean)
                : [];
          for (const s of siblingEmails) {
            if (s && !validateEmailDiff(value, s)) {
              return CO_APPLICANT_EMAIL_UNIQUE_MESSAGE;
            }
          }
        }
        if (!showCoApplicantFields && type === "edit" && invite?.co_applicant?.length && value) {
          const coApplicantEmails = invite.co_applicant
            .map((c: CoApplicant) => c.email)
            .filter(Boolean) as string[];
          for (const s of coApplicantEmails) {
            if (s && !validateEmailDiff(value, s)) {
              return CO_APPLICANT_EMAIL_UNIQUE_MESSAGE;
            }
          }
        }
        return undefined;
      }

      case "mobile_no":
        if (!value || !value.trim()) return "Mobile Number is required.";
        if (value && value.trim() && !validateMobile(value)) {
          return MOBILE_PHONE_PATTERN_MESSAGE;
        }
        const primaryMobileForCo =
          parentInvite?.mobile_no ?? (type === "add" ? invite?.mobile_no : undefined);
        if (showCoApplicantFields && primaryMobileForCo && value) {
          if (!validateMobileDiff(value, primaryMobileForCo)) {
            return CO_APPLICANT_PHONE_UNIQUE_MESSAGE;
          }
        }
        if (showCoApplicantFields && value && invite) {
          const siblingMobiles: string[] =
            type === "add" && invite.co_applicant?.length
              ? invite.co_applicant.map((c: CoApplicant) => c.mobile_no).filter(Boolean) as string[]
              : parentInvite?.co_applicant?.length && invite.invite_code
                ? (parentInvite.co_applicant as { invite_code?: string; mobile_no: string }[])
                    .filter((c) => c.invite_code !== invite.invite_code)
                    .map((c) => c.mobile_no)
                    .filter(Boolean)
                : [];
          for (const s of siblingMobiles) {
            if (s && !validateMobileDiff(value, s)) {
              return CO_APPLICANT_PHONE_UNIQUE_MESSAGE;
            }
          }
        }
        if (!showCoApplicantFields && type === "edit" && invite?.co_applicant?.length && value) {
          const coApplicantMobiles = invite.co_applicant
            .map((c: CoApplicant) => c.mobile_no)
            .filter(Boolean) as string[];
          for (const s of coApplicantMobiles) {
            if (s && !validateMobileDiff(value, s)) {
              return CO_APPLICANT_PHONE_UNIQUE_MESSAGE;
            }
          }
        }
        return undefined;

      case "relationship_type":
        if (showCoApplicantFields && (!value || !value.trim())) {
          return "Relationship is required.";
        }
        return undefined;

      case "living_unit":
        if (showCoApplicantFields && value !== "0" && value !== "1") {
          return "Please select an option for the above question.";
        }
        return undefined;

      case "is_approved_require":
        if (showCoApplicantFields && value !== "0" && value !== "1") {
          return "Liberty Approval Needed Selection is required.";
        }
        return undefined;

      case "income_verified":
        // If is_approved_require is '0', income_verified must be '1'
        if (vals.is_approved_require === "0" && value !== "1") {
          return "Income must be verified when Liberty Approval Needed is No.";
        }
        return undefined;

      case "income": {
        const digitLimitError = getIncomeDigitLimitValidationMessage(value);
        if (digitLimitError) return digitLimitError;
        // If income_verified is '1', income must be provided
        if (vals.income_verified === "1") {
          const incomeValue = value?.replace(/[^0-9.]/g, "") || "";
          if (!incomeValue || parseFloat(incomeValue) <= 0) {
            return "Please Enter Income Total.";
          }
        }
        // If is_approved_require is '0', income must be provided
        if (vals.is_approved_require === "0") {
          const incomeValue = value?.replace(/[^0-9.]/g, "") || "";
          if (!incomeValue || parseFloat(incomeValue) <= 0) {
            return "Please Enter Income Total.";
          }
        }
        return undefined;
      }

      default:
        return undefined;
    }
  };

  // Handle field blur - validate on blur
  const handleBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, values[name]);
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Handle field change
  const handleChange = (name: string, value: any) => {
    const newValues = { ...values, [name]: value };
    setValues(newValues);

    // Clear error when user starts typing (if field was touched)
    if (touched[name]) {
      const error = validateField(name, value, newValues);
      setFieldErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  // Handle name change with title case transformation (like Angular's titleName pipe)
  const handleNameChange = (name: string, value: string) => {
    const titleCased = toTitleCase(value);
    handleChange(name, titleCased);
  };

  // Handle email change with lowercase transformation (like Angular's lowercase pipe)
  const handleEmailChange = (value: string) => {
    handleChange("email", value.toLowerCase());
  };

  // Reset income_verified when is_approved_require changes (like Angular's resetVerifiedIncome)
  const handleApprovalChange = (value: string) => {
    const newValues = {
      ...values,
      is_approved_require: value,
      // If approval not needed (No), auto-set income_verified to '1'; otherwise reset
      income_verified: value === "0" ? "1" : "",
    };
    setValues(newValues);

    // When approval is needed ("Yes"), immediately surface income verification requirement.
    // When approval is not needed ("No"), keep/clear state as valid.
    if (value === "1") {
      setTouched((prev) => ({ ...prev, income_verified: true }));
    }

    // Validate if fields were touched
    if (touched.is_approved_require) {
      const error = validateField("is_approved_require", value, newValues);
      setFieldErrors((prev) => ({ ...prev, is_approved_require: error }));
    }
    const shouldValidateIncomeVerified = touched.income_verified || value === "1";
    if (shouldValidateIncomeVerified) {
      const error = validateField("income_verified", newValues.income_verified, newValues);
      setFieldErrors((prev) => ({ ...prev, income_verified: error }));
    } else if (value === "0") {
      setFieldErrors((prev) => ({ ...prev, income_verified: undefined }));
    }
  };

  const handleSelectIncomeNotVerified = () => {
    const newValues = {
      ...values,
      income_verified: "0",
      income: "",
    };
    setValues(newValues);
    setTouched((prev) => ({ ...prev, income_verified: true }));
    setFieldErrors((prev) => ({
      ...prev,
      income_verified: validateField("income_verified", "0", newValues),
      income: validateField("income", "", newValues),
    }));
  };

  // Validate all fields
  const validateAll = (): { isValid: boolean; firstError: string | null } => {
    const schema = showCoApplicantFields
      ? editCoApplicantSchema
      : editInviteeSchema;

    try {
      schema.parse(values);
      const emailErr = validateField("email", values.email, values);
      const mobileErr = validateField("mobile_no", values.mobile_no, values);
      if (emailErr || mobileErr) {
        const nextErrors: FieldErrors = {};
        if (emailErr) nextErrors.email = emailErr;
        if (mobileErr) nextErrors.mobile_no = mobileErr;
        const allTouched: Record<string, boolean> = {};
        Object.keys(values).forEach((key) => {
          allTouched[key] = true;
        });
        setTouched(allTouched);
        setFieldErrors(nextErrors);
        return {
          isValid: false,
          firstError: emailErr || mobileErr || null,
        };
      }
      setFieldErrors({});
      return { isValid: true, firstError: null };
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: FieldErrors = {};
        let firstError: string | null = null;

        (err as z.ZodError).issues.forEach((issue) => {
          const field = issue.path[0] as keyof FieldErrors;
          if (!errors[field]) {
            errors[field] = issue.message;
            if (!firstError) {
              firstError = issue.message;
            }
          }
        });

        const emailVErr = validateField("email", values.email, values);
        const mobileVErr = validateField("mobile_no", values.mobile_no, values);
        if (emailVErr) {
          errors.email = emailVErr;
          if (!firstError) firstError = emailVErr;
        }
        if (mobileVErr) {
          errors.mobile_no = mobileVErr;
          if (!firstError) firstError = mobileVErr;
        }

        // Mark all fields as touched
        const allTouched: Record<string, boolean> = {};
        Object.keys(values).forEach((key) => {
          allTouched[key] = true;
        });
        setTouched(allTouched);
        setFieldErrors(errors);

        return { isValid: false, firstError };
      }
      return { isValid: false, firstError: "Validation failed" };
    }
  };

  /** Co-applicant Add/Edit: submit enabled only when Zod + duplicate contact checks pass (same rules as submit). */
  const canSubmitCoApplicant = useMemo(() => {
    if (!showCoApplicantFields) return true;
    const parsed = editCoApplicantSchema.safeParse(values);
    if (!parsed.success) return false;
    const emailErr = validateField("email", values.email, values);
    const mobileErr = validateField("mobile_no", values.mobile_no, values);
    return !emailErr && !mobileErr;
  }, [showCoApplicantFields, values, invite, parentInvite, type]);

  const submit = async () => {
    if (!invite) return;

    const { isValid } = validateAll();

    if (!isValid) {
      setError(null);
      // Focus on first invalid field (inline field errors only; no summary banner)
      setTimeout(() => {
        const firstInvalid = document.querySelector(
          ".field-error",
        ) as HTMLElement;
        if (firstInvalid) {
          const input = firstInvalid
            .closest(".space-y-1\\.5")
            ?.querySelector("input, select") as HTMLElement;
          if (input) {
            input.focus();
            input.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }, 100);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const endpointCode = parentInvite?.invite_code || invite.invite_code;
      const payload: Record<string, any> = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        mobile_no: values.mobile_no,
        income_verified: values.income_verified,
        income: values.income,
      };

      if (showCoApplicantFields) {
        payload.relationship_type = values.relationship_type;
        payload.living_unit = values.living_unit;
        payload.is_approved_require = values.is_approved_require;
      }

      if (type !== "add") {
        payload.invite_code = invite.invite_code;
      }

      await updateInvitation(endpointCode, payload);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to save changes");
    } finally {
      setLoading(false);
    }
  };

  // Field error display component
  const FieldError = ({ error }: { error?: string }) => {
    if (!error) return null;
    return <p className="text-xs text-red-500 mt-1 field-error">{error}</p>;
  };

  // Check if we should show the info alert for approval not needed
  const showApprovalInfoAlert =
    showCoApplicantFields &&
    values.is_approved_require === "0" &&
    !values.income;

  const dialog = (
    <div
      data-testid="invitee-edit-dialog"
      className={`${modalStyles.modalOverlay} !z-[10001]`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        data-testid="invitee-edit-dialog-panel"
        className={`${modalStyles.modalContent} ${modalStyles.modalContent36} ${modalStyles.modalContentStacked}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`${modalStyles.modalHeaderConfirmTight} shrink-0 bg-white`}>
          <h3 data-testid="invitee-edit-dialog-title">
            {type === "add" ? "Add Co-Applicant" : "Edit Invite - Applicant Info"}
          </h3>
          <button
            type="button"
            data-testid="invitee-edit-dialog-close-button"
            className={modalStyles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div
          data-testid="invitee-edit-dialog-content"
          className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6"
        >
          {/* Meta Info Box */}
          <div
            data-testid="invitee-edit-dialog-meta-box"
            className="bg-slate-50 border border-slate-100 rounded-xl p-4 mb-6 text-sm text-slate-600 flex flex-col gap-2"
          >
            <div className="flex justify-between">
              <span className="font-semibold text-slate-700">
                {type === "add" ? "Primary Invite Code" : "Invite Code"}
              </span>
              <span className="font-mono text-slate-600">
                {invite?.invite_code}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold text-slate-700">
                {type === "add" ? "Primary Name" : "Name"}
              </span>
              <span>
                {invite?.first_name} {invite?.last_name}
              </span>
            </div>
          </div>

          {error && (
            <div
              data-testid="invitee-edit-dialog-error-banner"
              className="mb-6 bg-red-50 text-red-600 p-4 rounded-xl text-sm border border-red-100 flex items-start gap-2"
            >
              <X className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* First Name */}
            <div
              data-testid="invitee-edit-dialog-first-name-field"
              className="space-y-1.5"
            >
              <label
                data-testid="invitee-edit-dialog-first-name-label"
                className="text-sm font-semibold text-slate-700"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <FormInput
                data-testid="invitee-edit-dialog-first-name-input"
                value={values.first_name || ""}
                onChange={(e) => handleNameChange("first_name", e.target.value)}
                onBlur={() => handleBlur("first_name")}
                className={`w-full ${touched.first_name && fieldErrors.first_name ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}`}
                placeholder="Enter first name"
              />
              <FieldError
                error={touched.first_name ? fieldErrors.first_name : undefined}
              />
            </div>

            {/* Last Name */}
            <div
              data-testid="invitee-edit-dialog-last-name-field"
              className="space-y-1.5"
            >
              <label
                data-testid="invitee-edit-dialog-last-name-label"
                className="text-sm font-semibold text-slate-700"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <FormInput
                data-testid="invitee-edit-dialog-last-name-input"
                value={values.last_name || ""}
                onChange={(e) => handleNameChange("last_name", e.target.value)}
                onBlur={() => handleBlur("last_name")}
                className={`w-full ${touched.last_name && fieldErrors.last_name ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}`}
                placeholder="Enter last name"
              />
              <FieldError
                error={touched.last_name ? fieldErrors.last_name : undefined}
              />
            </div>

            {/* Email */}
            <div
              data-testid="invitee-edit-dialog-email-field"
              className="space-y-1.5 col-span-1 md:col-span-2"
            >
              <label
                data-testid="invitee-edit-dialog-email-label"
                className="text-sm font-semibold text-slate-700"
              >
                Email <span className="text-red-500">*</span>
              </label>
              <FormInput
                data-testid="invitee-edit-dialog-email-input"
                type="text"
                autoComplete="email"
                value={values.email || ""}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={() => handleBlur("email")}
                className={`w-full ${touched.email && fieldErrors.email ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}`}
                placeholder="Enter email address"
              />
              <FieldError
                error={touched.email ? fieldErrors.email : undefined}
              />
            </div>

            {/* Mobile */}
            <div
              data-testid="invitee-edit-dialog-mobile-field"
              className="space-y-1.5 col-span-1 md:col-span-2"
            >
              <label
                data-testid="invitee-edit-dialog-mobile-label"
                className="text-sm font-semibold text-slate-700"
              >
                Mobile Number (For Text Message){" "}
                <span className="text-red-500">*</span>
              </label>
              <FormInput
                data-testid="invitee-edit-dialog-mobile-input"
                value={formatPhoneNumber(values.mobile_no || "")}
                onChange={(e) => handleChange("mobile_no", e.target.value)}
                onBlur={() => handleBlur("mobile_no")}
                className={`w-full ${touched.mobile_no && fieldErrors.mobile_no ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}`}
                placeholder="Enter mobile number"
              />
              <FieldError
                error={touched.mobile_no ? fieldErrors.mobile_no : undefined}
              />
            </div>

            {showCoApplicantFields && (
              <>
                <div
                  data-testid="invitee-edit-dialog-relationship-field"
                  className="space-y-1.5 col-span-1 md:col-span-2"
                >
                  <label
                    data-testid="invitee-edit-dialog-relationship-label"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Relationship <span className="text-red-500">*</span>
                  </label>
                  <select
                    data-testid="invitee-edit-dialog-relationship-select"
                    className={`w-full px-4 py-2.5 border rounded-xl bg-white text-sm focus:outline-none focus:ring-2 transition-all ${touched.relationship_type && fieldErrors.relationship_type
                      ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                      : "border-slate-200 focus:ring-[rgb(21_151_255)]/15 focus:border-[rgb(21_151_255)]"
                      }`}
                    value={values.relationship_type || ""}
                    onChange={(e) =>
                      handleChange("relationship_type", e.target.value)
                    }
                    onBlur={() => handleBlur("relationship_type")}
                  >
                    <option value="">Select</option>
                    {Object.entries(RELATIONSHIP_TYPE_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </select>
                  <FieldError
                    error={
                      touched.relationship_type
                        ? fieldErrors.relationship_type
                        : undefined
                    }
                  />
                  {/* Co-signer note (like Angular) */}
                  {values.relationship_type === "CO_SIGNER" && (
                    <p className="text-xs text-amber-600 mt-1">
                      Please note, selecting co-signer means that the applicant
                      will not be moving into the selected unit.
                    </p>
                  )}
                </div>

                {/* Living Unit Question */}
                <div
                  data-testid="invitee-edit-dialog-living-unit-field"
                  className="space-y-2 col-span-1 md:col-span-2"
                >
                  <label
                    data-testid="invitee-edit-dialog-living-unit-label"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Will the applicant be living in the unit?{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="living_unit"
                        value="1"
                        checked={values.living_unit === "1"}
                        onChange={() => handleChange("living_unit", "1")}
                        onBlur={() => handleBlur("living_unit")}
                        className="w-4 h-4 text-[rgb(21_151_255)] border-slate-300 focus:ring-[rgb(21_151_255)]"
                        data-testid="invitee-edit-dialog-living-unit-yes-radio"
                      />
                      <span className="text-sm text-slate-600 group-hover:text-slate-800">
                        Yes
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="living_unit"
                        value="0"
                        checked={values.living_unit === "0"}
                        onChange={() => handleChange("living_unit", "0")}
                        onBlur={() => handleBlur("living_unit")}
                        className="w-4 h-4 text-[rgb(21_151_255)] border-slate-300 focus:ring-[rgb(21_151_255)]"
                        data-testid="invitee-edit-dialog-living-unit-no-radio"
                      />
                      <span className="text-sm text-slate-600 group-hover:text-slate-800">
                        No
                      </span>
                    </label>
                  </div>
                  <FieldError
                    error={
                      touched.living_unit ? fieldErrors.living_unit : undefined
                    }
                  />
                </div>

                {/* Approval Question */}
                <div
                  data-testid="invitee-edit-dialog-liberty-approval-field"
                  className="space-y-2 col-span-1 md:col-span-2"
                >
                  <label
                    data-testid="invitee-edit-dialog-liberty-approval-label"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Liberty Approval Needed?{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="is_approved_require"
                        value="1"
                        checked={values.is_approved_require === "1"}
                        onChange={() => handleApprovalChange("1")}
                        onBlur={() => handleBlur("is_approved_require")}
                        className="w-4 h-4 text-[rgb(21_151_255)] border-slate-300 focus:ring-[rgb(21_151_255)]"
                        data-testid="invitee-edit-dialog-liberty-approval-yes-radio"
                      />
                      <span className="text-sm text-slate-600 group-hover:text-slate-800">
                        Yes
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="is_approved_require"
                        value="0"
                        checked={values.is_approved_require === "0"}
                        onChange={() => handleApprovalChange("0")}
                        onBlur={() => handleBlur("is_approved_require")}
                        className="w-4 h-4 text-[rgb(21_151_255)] border-slate-300 focus:ring-[rgb(21_151_255)]"
                        data-testid="invitee-edit-dialog-liberty-approval-no-radio"
                      />
                      <span className="text-sm text-slate-600 group-hover:text-slate-800">
                        No
                      </span>
                    </label>
                  </div>
                  <FieldError
                    error={
                      touched.is_approved_require
                        ? fieldErrors.is_approved_require
                        : undefined
                    }
                  />
                </div>

                {/* Info alert when approval not needed and no income (like Angular) */}
                {showApprovalInfoAlert && (
                  <div
                    data-testid="invitee-edit-dialog-liberty-approval-info-alert"
                    className="col-span-1 md:col-span-2 bg-[rgb(21_151_255)]/[0.08] border border-[rgb(21_151_255)]/25 rounded-xl p-4 flex items-start gap-3"
                  >
                    <Info className="h-5 w-5 text-[rgb(21_151_255)] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-800">
                      <p>
                        <strong>Note:</strong> Only applicants who apply with
                        Liberty Rent and are approved will appear on the policy.
                        If your property requires all residents to be listed on
                        the policy please select "Yes".
                      </p>
                      <p className="mt-2">
                        We need the income verified for co-applicant with
                        "Liberty Approval Required" as "No".
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Income Verification Section – amount field always visible; typing sets Yes (issue #7) */}
            <div
              data-testid="invitee-edit-dialog-income-verification-section"
              className="col-span-1 md:col-span-2 bg-slate-50 border border-slate-200 rounded-xl p-4 mt-2"
            >
              <label
                data-testid="invitee-edit-dialog-income-verification-label"
                className="block text-sm font-bold text-slate-800 uppercase tracking-wider mb-4"
              >
                Income Verification <span className="text-red-500">*</span>
              </label>
              <div className="space-y-4">
                {/* Verified Option – radio and amount both usable first */}
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="radio"
                    name="income_verified"
                    value="1"
                    checked={values.income_verified === "1"}
                    onChange={() => handleChange("income_verified", "1")}
                    onBlur={() => handleBlur("income_verified")}
                    className="w-5 h-5 text-[rgb(21_151_255)] border-slate-300 focus:ring-[rgb(21_151_255)] flex-shrink-0"
                    id="invitee-edit-dialog-income-verified-yes"
                    data-testid="invitee-edit-dialog-income-verified-yes-radio"
                  />
                  <label
                    htmlFor="invitee-edit-dialog-income-verified-yes"
                    className="text-sm font-medium text-slate-700 cursor-pointer"
                  >
                    Monthly Income verified as
                  </label>
                  <div className="relative w-40">
                    <CurrencyInput
                      data-testid="invitee-edit-dialog-income-input"
                      maxDigitsBeforeDecimal={INCOME_MAX_DIGITS_BEFORE_DECIMAL}
                      value={values.income || ""}
                      onChange={(v) => {
                        handleChange("income", v);
                        if (v && v.replace(/[^0-9.]/g, "").length > 0 && values.income_verified !== "1") {
                          handleChange("income_verified", "1");
                        }
                      }}
                      onFocus={() => {
                        if (values.income_verified !== "0") {
                          handleChange("income_verified", "1");
                        }
                      }}
                      onBlur={() => handleBlur("income")}
                      placeholder="Income Total"
                      className={`py-1.5 h-9 text-sm ${touched.income && fieldErrors.income ? "border-red-500" : ""}`}
                    />
                  </div>
                  <span className="text-sm text-slate-500">(approximately)</span>
                </div>
                {(values.income_verified === "1" || (touched.income && fieldErrors.income)) && (
                  <FieldError error={fieldErrors.income} />
                )}

                {/* Not Verified Option – show when approval is needed; whole row clickable; single state update so selection sticks */}
                {values.is_approved_require !== "0" && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={handleSelectIncomeNotVerified}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelectIncomeNotVerified();
                      }
                    }}
                    className="flex items-center gap-3 cursor-pointer w-fit min-h-[28px] relative z-10"
                  >
                    <input
                      type="radio"
                      name="income_verified"
                      value="0"
                      checked={values.income_verified === "0"}
                      onChange={handleSelectIncomeNotVerified}
                      onBlur={() => handleBlur("income_verified")}
                      className="w-5 h-5 text-[rgb(21_151_255)] border-slate-300 focus:ring-[rgb(21_151_255)] flex-shrink-0 cursor-pointer pointer-events-auto"
                      id="invitee-edit-dialog-income-verified-no"
                      aria-checked={values.income_verified === "0"}
                      data-testid="invitee-edit-dialog-income-verified-no-radio"
                    />
                    <span className="text-sm font-medium text-slate-700 select-none pointer-events-none">
                      Not Verified
                    </span>
                  </div>
                )}
              </div>
              <FieldError
                error={
                  touched.income_verified
                    ? fieldErrors.income_verified
                    : undefined
                }
              />
            </div>
          </div>
        </div>

        <div
          data-testid="invitee-edit-dialog-footer"
          className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-[#e5e7eb] bg-[#f9fafb] px-5 py-3 sm:px-6"
        >
          <button
            type="button"
            data-testid="invitee-edit-dialog-cancel-button"
            className="px-5 py-2.5 bg-white text-slate-600 font-semibold rounded-xl border border-slate-200 hover:bg-slate-50 hover:text-slate-800 transition-all text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="invitee-edit-dialog-submit-button"
            className="px-5 py-2.5 bg-[rgb(21_151_255)] text-white font-semibold rounded-xl shadow-lg shadow-[rgba(21,151,255,0.25)] hover:bg-[#0d7acc] hover:shadow-xl transition-all flex items-center gap-2 text-sm enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:hover:bg-[rgb(21_151_255)]"
            disabled={loading || !canSubmitCoApplicant}
            onClick={submit}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Submit
          </button>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return createPortal(dialog, document.body);
}
